import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose } from 'ramda';
import { Layout } from 'layouts';
import { PageTitle } from 'components/misc';
import { Error, HugggLife, Reaction } from 'components';
import { Divider, useThemeContext } from '@huggg/react-components';
import {
  RedeemMethodPinContainer,
  RedeemMethodVoucherContainer
} from 'components/Redeem';
import { withLocations, withHuggg, withRedirect } from 'hoc';
import { useAnalytics } from 'hooks';
import { getBasePathname } from 'utilities/huggg';
import styles from './redeem.module.scss';
import { isInIframe } from 'utilities/iframes';
import { isRedeeming } from 'redux/modules/redeem';
import {
  getSelectedSegment,
  setSelectedSegment
} from 'redux/modules/selectedSegment';
import { getRedeemedSegment, getSegments } from 'redux/modules/huggg';

export const ScreensRedeem = props => {
  const {
    huggg,
    isBlueHuggg,
    isPinRedeem,
    isRedeemed,
    isValidRedemptionMethod,
    vouchers,
    segments,
    setSelectedSegment
  } = props;
  const analytics = useAnalytics();
  const backAction = useCallback(() => getBasePathname(huggg), [huggg]);
  const hasVouchers = Boolean(vouchers && vouchers.length);

  useEffect(() => {
    analytics.screenViewedHugggRedeem();
  }, [analytics]);

  useEffect(() => {
    if (segments?.length === 1) {
      setSelectedSegment(segments[0]);
    }
  }, [segments]);

  const { name: theme } = useThemeContext();

  const shouldShowHugggLife =
    (!theme || theme === 'default') &&
    !isBlueHuggg &&
    (isRedeemed || hasVouchers) &&
    !isInIframe();

  const hasReaction = huggg?.hasReaction;

  // If it doesn't exist or it is less than 30 seconds old, it is the first redemption
  // A bit hacky but more reliable than checking if the huggg has been redeemed
  const isFirstRedemption = useMemo(() => {
    try {
      const redeemedCreatedAt = huggg?.redeemed?.created_at;
      if (!redeemedCreatedAt) return true;

      const redeemedAtDate = new Date(redeemedCreatedAt);
      return redeemedAtDate.getSeconds() < 15;
    } catch (e) {
      return false;
    }
  }, [huggg]);

  const shouldShowReaction = useMemo(
    () => !hasReaction && isFirstRedemption && (isRedeemed || hasVouchers),
    [hasReaction, isRedeemed, isFirstRedemption, hasVouchers]
  );

  return (
    <Layout backAction={backAction}>
      <PageTitle huggg={huggg} />

      <div data-test-id="screen-redeem">
        {isValidRedemptionMethod ? (
          <>
            <div>
              {isPinRedeem ? (
                <RedeemMethodPinContainer data-test-id="redeem-method-pin" />
              ) : (
                <RedeemMethodVoucherContainer data-test-id="redeem-method-voucher" />
              )}
            </div>
          </>
        ) : (
          <Error
            data-test-id="redeem-method-unknown"
            errorMessage="Unknown redemption method"
          />
        )}
      </div>

      <Divider className={styles.divider} />

      {shouldShowReaction && (
        <div className={styles.reaction}>
          <Reaction includeCommentSection={true} isDialog />
          {/* <Divider /> */}
        </div>
      )}

      {shouldShowHugggLife && (
        <div className={styles.hugggLifeContainer}>
          <HugggLife />
        </div>
      )}
    </Layout>
  );
};

ScreensRedeem.propTypes = {
  huggg: PropTypes.object.isRequired,
  isBlueHuggg: PropTypes.bool.isRequired,
  isFormRedeemed: PropTypes.bool.isRequired,
  isMultiRedeemable: PropTypes.bool.isRequired,
  isPinRedeem: PropTypes.bool.isRequired,
  isValidRedemptionMethod: PropTypes.bool.isRequired,
  isRedeemable: PropTypes.bool.isRequired,
  isRedeemed: PropTypes.bool.isRequired,
  isRedeeming: PropTypes.bool.isRequired,
  isTestHuggg: PropTypes.bool.isRequired,
  vouchers: PropTypes.array
};

export const redeemedRedirectHandler = props => {
  const { isRedeemed, isMultiRedeemable } = props;

  const shouldRedirect = !isMultiRedeemable && isRedeemed;

  return [shouldRedirect, `${getBasePathname(props.huggg)}/redeemed`];
};

export const redeemableRedirectHandler = props => [
  !props.isRedeemable,
  `${getBasePathname(props.huggg)}/stash`
];

export const chooseRedirectHandler = props => {
  const { segments, huggg, selectedSegment, redeemedSegment } = props;
  const isMultiSegment = segments?.length > 1;

  return [
    isMultiSegment && !selectedSegment && !redeemedSegment,
    `${getBasePathname(huggg)}/choose`
  ];
};

const augmentedWithHuggg = screen =>
  withHuggg(screen, { forceRefresh: { huggg: true } });

const mapStateToProps = state => ({
  isRedeeming: isRedeeming(state),
  segments: getSegments(state),
  selectedSegment: getSelectedSegment(state),
  redeemedSegment: getRedeemedSegment(state)
});

export default compose(
  augmentedWithHuggg,
  connect(mapStateToProps, { setSelectedSegment }),
  withLocations,
  withRedirect(redeemedRedirectHandler),
  withRedirect(redeemableRedirectHandler),
  withRedirect(chooseRedirectHandler)
)(ScreensRedeem);
